import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
} from "@mui/material";

function PersonCard({ title, person }) {
  if (!person) return null;
  return (
    <Card sx={{ mb: 2, width: 320, height: 280, flex: "0 0 auto", mx: 1 }}>
      <CardContent>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          {title}
        </Typography>
        <Typography fontWeight={600}>
          {[person.firstName, person.middleName, person.lastName]
            .filter(Boolean)
            .join(" ") ||
            person.fullName ||
            person.usefMemberOfficialName ||
            "-"}
        </Typography>
        {person.email && (
          <Typography variant="body2" color="text.secondary">
            Email: {person.email}
          </Typography>
        )}
        {person.phone &&
          Array.isArray(person.phone) &&
          person.phone.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              Phone:{" "}
              {person.phone
                .map((p) => p.phoneNumber)
                .filter(Boolean)
                .join(", ")}
            </Typography>
          )}
        {(person.usdfMembershipNumber || person.usefMembershipNumber) && (
          <Typography variant="body2" color="text.secondary">
            {person.usdfMembershipNumber
              ? `USDF #: ${person.usdfMembershipNumber}`
              : ""}
            {person.usdfMembershipNumber && person.usefMembershipNumber
              ? " | "
              : ""}
            {person.usefMembershipNumber
              ? `USEF #: ${person.usefMembershipNumber}`
              : ""}
          </Typography>
        )}
        {person.orgUsdfMembershipNumber && (
          <Typography variant="body2" color="text.secondary">
            Org USDF #: {person.orgUsdfMembershipNumber}
          </Typography>
        )}
        {person.orgUsefMembershipNumber && (
          <Typography variant="body2" color="text.secondary">
            Org USEF #: {person.orgUsefMembershipNumber}
          </Typography>
        )}
        {person.dateOfBirth && (
          <Typography variant="body2" color="text.secondary">
            DOB: {person.dateOfBirth}
          </Typography>
        )}
        {person.citizenship && (
          <Typography variant="body2" color="text.secondary">
            Citizenship: {person.citizenship}
          </Typography>
        )}
        {person.isOrg && (
          <Typography variant="body2" color="text.secondary">
            Organization
          </Typography>
        )}
        {person.address && (
          <Typography variant="body2" color="text.secondary">
            Address:{" "}
            {[
              person.address.address1,
              person.address.city,
              person.address.stateProvinceRegion,
              person.address.postalCode,
            ]
              .filter(Boolean)
              .join(", ")}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

function HorseCard({ horse }) {
  if (!horse) return null;
  return (
    <Card sx={{ mb: 2, width: 320, height: 280, flex: "0 0 auto", mx: 1 }}>
      <CardContent>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Horse
        </Typography>
        <Typography fontWeight={600}>{horse.name}</Typography>
        {horse.breed && (
          <Typography variant="body2" color="text.secondary">
            Breed: {horse.breed}
          </Typography>
        )}
        {horse.color && (
          <Typography variant="body2" color="text.secondary">
            Color: {horse.color}
          </Typography>
        )}
        {horse.sex && (
          <Typography variant="body2" color="text.secondary">
            Sex: {horse.sex}
          </Typography>
        )}
        {horse.dateOfBirth && (
          <Typography variant="body2" color="text.secondary">
            DOB: {horse.dateOfBirth}
          </Typography>
        )}

        {(horse.usdfMembershipNumber || horse.usefMembershipNumber) && (
          <Typography variant="body2" color="text.secondary">
            {horse.usdfMembershipNumber
              ? `USDF #: ${horse.usdfMembershipNumber}`
              : ""}
            {horse.usdfMembershipNumber && horse.usefMembershipNumber
              ? " | "
              : ""}
            {horse.usefMembershipNumber
              ? `USEF #: ${horse.usefMembershipNumber}`
              : ""}
          </Typography>
        )}

        {horse.countryOfBirth && (
          <Typography variant="body2" color="text.secondary">
            Country of Birth: {horse.countryOfBirth}
          </Typography>
        )}
        {horse.heightInHands && (
          <Typography variant="body2" color="text.secondary">
            Height: {horse.heightInHands} hands
          </Typography>
        )}
        {horse.isOwnedByOrg && (
          <Typography variant="body2" color="text.secondary">
            Owned by Organization
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * A component for displaying the content of an entry detail tab
 * @param {Object} props
 * @param {Object} props.entry - The entry data object
 * @param {number} props.tabIndex - The index of the currently selected tab
 */
export default function EntryDetailTabPanel({ entry, tabIndex }) {
  const renderTabContent = () => {
    switch (tabIndex) {
      case 0: // General
        return (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              justifyContent: "flex-start",
              alignItems: "flex-start",
              width: "100%",
              maxWidth: "100%",
              overflowX: "visible",
            }}
          >
            <HorseCard horse={entry.horse} />
            <PersonCard title="Rider" person={entry.rider} />
            {entry.trainer && (
              <PersonCard title="Trainer" person={entry.trainer} />
            )}
            {entry.coach && <PersonCard title="Coach" person={entry.coach} />}
            {entry.horse?.horseOwner &&
              typeof entry.horse.horseOwner === "object" && (
                <PersonCard title="Owner" person={entry.horse.horseOwner} />
              )}
          </Box>
        );
      case 1: // Documents
        return (
          <Box sx={{ p: 2 }}>
            <Typography variant="body1">Documents tab coming soon.</Typography>
          </Box>
        );
      case 2: // Classes
        return (
          <>
            {(entry.classes || []).map((cls, idx) => (
              <Box
                key={idx}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Typography>{cls.name}</Typography>
                <Typography>${cls.fee}</Typography>
              </Box>
            ))}
          </>
        );
      case 3: // Stabling
        return (
          <Box sx={{ p: 2 }}>
            <Typography variant="body1">Stabling tab coming soon.</Typography>
          </Box>
        );
      case 4: // Refunds
        return (
          <>
            {(entry.refunds || []).map((r, idx) => (
              <Box
                key={idx}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Typography>
                  {r.date} — {r.reason}
                </Typography>
                <Typography>-${r.amount}</Typography>
              </Box>
            ))}
            <Button variant="outlined" sx={{ mt: 2 }}>
              Issue Refund
            </Button>
          </>
        );
      case 5: // Add-Ons
        return (
          <>
            {(entry.addons || []).map((a, idx) => (
              <Box
                key={idx}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Typography>
                  {a.name} × {a.qty}
                </Typography>
                <Typography>${a.qty * a.unitPrice}</Typography>
              </Box>
            ))}
            <Button variant="outlined" sx={{ mt: 2 }}>
              Add Item
            </Button>
          </>
        );
      default:
        return null;
    }
  };

  return <Box>{renderTabContent()}</Box>;
}
